import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { switchMap, catchError, filter, take, finalize } from 'rxjs/operators';
import { LoginService } from '../services/login.service';
import { environment } from '../../environments/environment';
import { StorageService } from '../services/storage.service';
// import { isEmbedded } from '../utils/iframe.util';
import { EbayService } from '../services/ebay.service';
import { Router } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private router = inject(Router);
  constructor(
    private authService: LoginService,
    private storageService: StorageService,
    private ebaySrv: EbayService,
  ) {}

  // useDemoApi = isEmbedded();
  platformId = inject(PLATFORM_ID);
  private API_URL = environment.apiUrl;

  private isRefreshing = false;
  private refreshSubject = new BehaviorSubject<string | null>(null);

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const accessToken = this.authService.getAccessToken();
    const csrfToken = this.getCsrfTokenFromCookie();
    const demoUrl = this.storageService.getCookie('apiUrl') == 'demo' ? true : false;
    if (req.url.startsWith(this.API_URL + '/api') && demoUrl) {
      const newUrl = req.url.replace(this.API_URL, 'https://demo.thumshop.de');
      const updatedRequest = req.clone({ url: newUrl });
      return this.handleRequestWithToken(updatedRequest, accessToken, csrfToken, next);
    }
    const updatedRequest = req.clone();

    return this.handleRequestWithToken(updatedRequest, accessToken, csrfToken, next);
  }

  private handleRequestWithToken(
    req: HttpRequest<any>,
    token: string | null,
    csrfToken: string | null,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const headers: { [key: string]: string } = {};
    const demoUrl = this.storageService.getCookie('apiUrl') == 'demo' ? true : false;
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    } else {
      if (!demoUrl && this.authService.getRefreshToken() == null) {
        this.authService.logout();
        this.router.navigate(['/']);
      }
    }
    if (csrfToken) headers['X-XSRF-TOKEN'] = csrfToken;

    if (Object.keys(headers).length > 0) {
      req = req.clone({ setHeaders: headers });
    }

    // Doppelte Slashes /tool fixen

    return next
      .handle(req)
      .pipe(catchError((error) => this.handleError(error, req, csrfToken, next)));
  }
  // Statt window.location zu verwenden

  private handleError(
    error: any,
    req: HttpRequest<any>,
    csrfToken: string | null,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (!(error instanceof HttpErrorResponse)) {
      return throwError(() => error);
    }

    const status = error.status;
    const message = this.extractMessage(error);
    if (status === 401) {
      if (message?.toLowerCase().includes('revoked')) {
        this.authService.logout();
        this.router.navigate(['/']);
        return throwError(() => new Error('Refresh token revoked'));
      }

      // if (message?.toLocaleLowerCase().includes('ebay')) {
      //   this.ebaySrv.auth();
      // }
      if (message?.toLocaleLowerCase().includes('invalid token')) {
        return this.handle401(req, next, csrfToken);
      } else if (message?.toLocaleLowerCase().includes('no authorization token was found')) {
        this.authService.logout();
        this.router.navigate(['/']);
      }
    }

    if (status === 403 && message?.toLowerCase().includes('csrf')) {
      return this.handleCsrf(req, next);
    }

    // if (status === 400 && message?.toLowerCase().includes('token')) {
    //   try {
    //     this.ebaySrv.auth();
    //   } catch (e) {
    //     console.error('ebay auth failed', e);
    //   }
    //   return throwError(() => new Error('Bad token — reauthenticate'));
    // }

    return throwError(() => error);
  }

  private handle401(
    req: HttpRequest<any>,
    next: HttpHandler,
    csrfToken: string | null,
  ): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshSubject.next(null);

      return this.authService.refreshToken().pipe(
        switchMap(() => {
          const newToken = this.authService.getAccessToken();
          if (!newToken) {
            this.authService.logout();
            this.router.navigate(['/']);
            return throwError(() => new Error('No access token after refresh'));
          }
          this.refreshSubject.next(newToken);
          return this.handleRequestWithToken(req, newToken, csrfToken, next);
        }),
        catchError((err) => {
          this.authService.logout();
          this.router.navigate(['/']);
          return throwError(() => err);
        }),
        finalize(() => (this.isRefreshing = false)),
      );
    } else {
      return this.refreshSubject.pipe(
        filter((token) => token != null),
        take(1),
        switchMap((token) => this.handleRequestWithToken(req, token, csrfToken, next)),
      );
    }
  }

  private handleCsrf(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshSubject.next(null);

      return this.authService.refreshCsrfToken().pipe(
        switchMap(() => {
          const csrf = this.authService.getCsrfTokenFromCookie();
          const access = this.authService.getAccessToken();
          this.refreshSubject.next('csrf-ok');
          return this.handleRequestWithToken(req, access, csrf, next);
        }),
        catchError((err) => {
          this.authService.logout();
          this.router.navigate(['/']);
          return throwError(() => err);
        }),
        finalize(() => (this.isRefreshing = false)),
      );
    } else {
      return this.refreshSubject.pipe(
        filter((val) => val != null),
        take(1),
        switchMap(() => {
          const csrf = this.authService.getCsrfTokenFromCookie();
          const access = this.authService.getAccessToken();
          return this.handleRequestWithToken(req, access, csrf, next);
        }),
      );
    }
  }

  private extractMessage(error: HttpErrorResponse): string | null {
    if (!error || error.error === undefined || error.error === null) return null;
    if (typeof error.error === 'string') return error.error;
    if (typeof error.error === 'object') {
      return (error.error.type || error.error.message || JSON.stringify(error.error)) as string;
    }
    return null;
  }

  private getCsrfTokenFromCookie(): string | null {
    if (isPlatformBrowser(this.platformId)) {
      const cookies = document.cookie.split(';').reduce(
        (acc, cookie) => {
          const [key, value] = cookie.trim().split('=');
          acc[key] = decodeURIComponent(value || '');
          return acc;
        },
        {} as { [key: string]: string },
      );
      return cookies['XSRF-TOKEN'] || null;
    }
    return null;
  }
}
